/*
 * Copyright (c) 2022 CalAmp Corp. All Rights Reserved
 */
import {
  MessageEndpoint,
  MessageEndpointConfiguration,
  MessageEndpointNames,
  MessageEndpointTypes
} from '@ion/core/src/types/entities';
import {isEmpty} from '@ion/core/src/utils/StringUtil';
import {FormState} from '@ion/dls/src/hooks/useFormState';

export enum MessageEndpointFormFields {
  TYPE = 'type',
  AWS_ACCESS_KEY = 'awsAccessKeyId',
  AWS_SECRET_ACCESS_KEY = 'awsSecretAccessKey',
  REGION = 'region',
  QUEUE_NAME = 'queueName',
  STREAM_NAME = 'streamName',
  BOOTSTRAP_SERVERS = 'bootstrapServers',
  KAFKA_TOPIC_NAME = 'topicName',
  USERNAME = 'username',
  PASSWORD = 'password'
}

const required = {required: true};

export const getInitialKafkaFormState = (
  currentType: MessageEndpointTypes,
  exisitingMessageEndpoint?: MessageEndpointConfiguration
): FormState => {
  const isEdit = !!exisitingMessageEndpoint;
  const isKafkaPlainOrScram = getIsKafkaPlainOrScramType(currentType);
  return {
    [MessageEndpointFormFields.BOOTSTRAP_SERVERS] : {
      ...required,
      name  : MessageEndpointFormFields.BOOTSTRAP_SERVERS,
      value : exisitingMessageEndpoint?.bootstrapServers || ''
    },
    [MessageEndpointFormFields.KAFKA_TOPIC_NAME] : {
      ...required,
      name  : MessageEndpointFormFields.KAFKA_TOPIC_NAME,
      value : exisitingMessageEndpoint?.topicName || ''
    },
    [MessageEndpointFormFields.USERNAME] : {
      required : isKafkaPlainOrScram,
      name     : MessageEndpointFormFields.USERNAME,
      value    : exisitingMessageEndpoint?.username || ''
    },
    [MessageEndpointFormFields.PASSWORD] : {
      required : isKafkaPlainOrScram && !isEdit,
      name     : MessageEndpointFormFields.PASSWORD,
      value    : exisitingMessageEndpoint?.password || ''
    }
  };
};

export const getInitialStreamFormState = (
  currentType: MessageEndpointTypes,
  exisitingMessageEndpoint?: MessageEndpointConfiguration
): FormState => {
  const isEdit = !!exisitingMessageEndpoint;
  const isStream = currentType === MessageEndpointTypes.AWS_KINESIS;
  return {
    [MessageEndpointFormFields.AWS_ACCESS_KEY] : {
      required : !isEdit,
      name     : MessageEndpointFormFields.AWS_ACCESS_KEY,
      value    : exisitingMessageEndpoint?.awsAccessKeyId || ''
    },
    [MessageEndpointFormFields.AWS_SECRET_ACCESS_KEY] : {
      required : !isEdit,
      name     : MessageEndpointFormFields.AWS_SECRET_ACCESS_KEY,
      value    : exisitingMessageEndpoint?.awsSecretAccessKey || ''
    },
    [MessageEndpointFormFields.REGION] : {
      ...required,
      name  : MessageEndpointFormFields.REGION,
      value : exisitingMessageEndpoint?.region || ''
    },
    [MessageEndpointFormFields.STREAM_NAME] : {
      required : isStream,
      name     : MessageEndpointFormFields.STREAM_NAME,
      value    : isStream ? exisitingMessageEndpoint?.streamName || '' : null
    },
    [MessageEndpointFormFields.QUEUE_NAME] : {
      required : !isStream,
      name     : MessageEndpointFormFields.QUEUE_NAME,
      value    : !isStream ? exisitingMessageEndpoint?.queueName || '' : null
    }
  };
};

// Serializes an Account's Message Endpoint for API consumption.
export const serializeMessageEndpoint = ({configuration, ...rest}: MessageEndpoint): MessageEndpoint => {
  const serializedConfiguration: MessageEndpointConfiguration = {};
  for (const [key, obj] of Object.entries(configuration)) {
    serializedConfiguration[key] = obj?.value;
  }
  removeSecureField(serializedConfiguration, 'awsSecretAccessKey');
  removeSecureField(serializedConfiguration, 'password');
  return {...rest, configuration: serializedConfiguration} as MessageEndpoint;
};

const removeSecureField = (configuration: MessageEndpointConfiguration, field): MessageEndpointConfiguration => {
  const value = configuration?.[field];
  if (!value?.length) {
    delete configuration[field];
  }
  return configuration;
};

// Validates the Message Endpoint fields & updates the state with any found.
export const validateMessageEndpoint = (messageEndpointFormState: FormState) => {
  const errors = {};
  for (const [name, value] of Object.entries(messageEndpointFormState)) {
    if ((value?.required && isEmpty(value.value)) || value?.error) {
      errors[name] = {...value, error: true};
    }
  }
  return errors;
};

// Returns the respective message endpoint name for the given message endpoint type.
export const getMessageName = (type: MessageEndpointTypes): MessageEndpointNames => {
  return type === MessageEndpointTypes.AWS_KINESIS
    ? MessageEndpointNames.STREAM_NAME : MessageEndpointNames.QUEUE_NAME;
};

// Returns the respective message endpoint type (returning Kinesis as the default if not present).
export const getMessageType = (formState: FormState): MessageEndpointTypes => {
  return MessageEndpointTypes[formState[MessageEndpointFormFields.TYPE]?.value] || MessageEndpointTypes.AWS_KINESIS;
};

// Returns the boolean value for the given Kafka message endpoint type.
export const getIsKafkaType = (type: MessageEndpointTypes): boolean => (
  type === MessageEndpointTypes.KAFKA_MTLS ||
  type === MessageEndpointTypes.KAFKA_SASL_PLAIN ||
  type === MessageEndpointTypes.KAFKA_SASL_SCRAM ||
  false
);

export const getIsStreamType = (type: MessageEndpointTypes): boolean => {
  const {AWS_KINESIS, AWS_SQS} = MessageEndpointTypes;
  return [AWS_KINESIS, AWS_SQS].includes(type) || false;
};

// Returns the boolean value for the given Kafka Plain Or Scram message endpoint type.
export const getIsKafkaPlainOrScramType = (type: MessageEndpointTypes): boolean => (
  type === MessageEndpointTypes.KAFKA_SASL_PLAIN ||
  type === MessageEndpointTypes.KAFKA_SASL_SCRAM ||
  false
);
